import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import {
  CreditCard, RefreshCw, Loader2, AlertCircle, CheckCircle2, AlertTriangle,
  Eye, EyeOff, Save, ShieldCheck,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StripeConfigPage = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ secret_key: '', publishable_key: '', webhook_secret: '' });
  const [showSecrets, setShowSecrets] = useState({ secret_key: false, webhook_secret: false });
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${API}/admin/stripe-config`, { headers, timeout: 12000 });
      setStatus(data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const { data } = await axios.put(`${API}/admin/stripe-config`, form, { headers, timeout: 15000 });
      setSuccess(`Clés enregistrées avec succès — mode ${data.livemode ? 'LIVE' : 'TEST'}. Configuration appliquée immédiatement.`);
      setEditOpen(false);
      setForm({ secret_key: '', publishable_key: '', webhook_secret: '' });
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur de validation');
    } finally {
      setSaving(false);
    }
  };

  const config = status?.config || {};
  const ping = status?.stripe_ping || {};
  const isLive = config.livemode_inferred;
  const pingOk = ping.ok;
  const sourceLabel = config.source === 'db'
    ? 'Configurée via l\'admin (base de données)'
    : 'Configurée via variables d\'environnement';

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="stripe-config-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <CreditCard className="w-6 h-6 text-[#52a675]" /> Configuration Stripe
          </h1>
          <p className="text-sm text-gray-500">Modifie les 3 clés Stripe sans redéploiement. Validation auto contre Stripe avant sauvegarde.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={load} variant="outline" size="sm" disabled={loading} data-testid="btn-stripe-refresh">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Actualiser
          </Button>
          <Button onClick={() => setEditOpen(true)} className="bg-[#52a675] hover:bg-[#3d8a5c]" size="sm" data-testid="btn-stripe-edit">
            <Save className="w-4 h-4 mr-1" /> Modifier les clés
          </Button>
        </div>
      </div>

      {success && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">{success}</AlertDescription>
        </Alert>
      )}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div className="py-12 text-center text-gray-500">
          <Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Chargement…
        </div>
      ) : (
        <>
          {/* Statut global */}
          <Card className={isLive ? 'border-2 border-red-300 bg-red-50/30' : 'border-2 border-amber-300 bg-amber-50/30'}>
            <CardContent className="p-5 flex items-center gap-4 flex-wrap">
              <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${isLive ? 'bg-red-100' : 'bg-amber-100'}`}>
                {isLive ? <ShieldCheck className="w-7 h-7 text-red-700" /> : <AlertTriangle className="w-7 h-7 text-amber-700" />}
              </div>
              <div className="flex-1 min-w-[200px]">
                <div className="text-xs uppercase tracking-wider text-gray-500">Mode Stripe actuel</div>
                <div className="text-2xl font-bold">
                  {isLive ? <span className="text-red-700">LIVE (production)</span> : <span className="text-amber-700">TEST (sandbox)</span>}
                </div>
                <div className="text-xs text-gray-500">{sourceLabel}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-gray-500">Ping Stripe</div>
                {pingOk ? (
                  <Badge className="bg-green-100 text-green-800 border-green-200 hover:bg-green-100"><CheckCircle2 className="w-3 h-3 mr-1" /> OK</Badge>
                ) : (
                  <Badge variant="destructive"><AlertCircle className="w-3 h-3 mr-1" /> Erreur</Badge>
                )}
                {!pingOk && ping.error && <div className="text-[11px] text-red-600 mt-1 max-w-xs">{ping.error}</div>}
              </div>
            </CardContent>
          </Card>

          {/* Clés actuelles */}
          <Card>
            <CardContent className="p-5 space-y-4">
              <h2 className="font-semibold text-gray-900">Clés actuellement utilisées</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500">Clé secrète</Label>
                  <div className="mt-1 font-mono text-sm bg-gray-50 border rounded-md p-2 break-all" data-testid="current-secret-key">
                    {config.secret_key_mask || <span className="text-gray-400 italic">(vide)</span>}
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500">Clé publishable</Label>
                  <div className="mt-1 font-mono text-sm bg-gray-50 border rounded-md p-2 break-all" data-testid="current-publishable-key">
                    {config.publishable_key || <span className="text-gray-400 italic">(vide)</span>}
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs uppercase tracking-wider text-gray-500">Webhook secret</Label>
                  <div className="mt-1 font-mono text-sm bg-gray-50 border rounded-md p-2 break-all" data-testid="current-webhook-secret">
                    {config.webhook_secret_mask || <span className="text-gray-400 italic">(vide)</span>}
                  </div>
                </div>
              </div>
              {pingOk && ping.balance && (
                <div className="border-t pt-3 mt-3">
                  <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Solde Stripe</div>
                  <div className="text-sm">
                    {ping.balance.length === 0 ? (
                      <span className="text-gray-500">Aucun solde disponible</span>
                    ) : (
                      ping.balance.map((b, i) => (
                        <span key={i} className="inline-block mr-3">
                          {(b.amount / 100).toFixed(2)} {b.currency?.toUpperCase()}
                        </span>
                      ))
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Aide */}
          <Card className="bg-gradient-to-br from-[#e8f5ef]/60 to-white border-[#52a675]/30">
            <CardContent className="p-5 text-sm text-gray-700 space-y-2">
              <h3 className="font-semibold text-gray-900">Comment changer les clés Stripe</h3>
              <ol className="list-decimal pl-5 space-y-1">
                <li>Dans Stripe dashboard, va sur <strong>Developers → API keys</strong>, bascule en mode LIVE</li>
                <li>Si tu veux roller la clé secrète actuelle : à côté de <code>sk_live_…</code> → <strong>Roll key</strong></li>
                <li>Récupère les 3 valeurs : <code>sk_live_…</code>, <code>pk_live_…</code>, et le <code>whsec_…</code> du webhook</li>
                <li>Clique sur <strong>"Modifier les clés"</strong> ci-dessus et colle-les</li>
                <li>L'app valide automatiquement contre Stripe avant d'enregistrer — pas de redéploiement requis</li>
              </ol>
            </CardContent>
          </Card>
        </>
      )}

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Modifier les clés Stripe</DialogTitle>
            <DialogDescription>
              Les 3 clés doivent provenir du même compte Stripe et du même mode (toutes TEST ou toutes LIVE). La validation se fait via un ping Stripe avant enregistrement.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="sk">Clé secrète (sk_live_… ou sk_test_…)</Label>
              <div className="relative">
                <Input
                  id="sk"
                  type={showSecrets.secret_key ? 'text' : 'password'}
                  value={form.secret_key}
                  onChange={(e) => setForm((p) => ({ ...p, secret_key: e.target.value }))}
                  placeholder="sk_live_…"
                  required
                  className="font-mono text-sm pr-10"
                  data-testid="input-stripe-secret-key"
                />
                <button
                  type="button"
                  onClick={() => setShowSecrets((p) => ({ ...p, secret_key: !p.secret_key }))}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  {showSecrets.secret_key ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="pk">Clé publishable (pk_live_… ou pk_test_…)</Label>
              <Input
                id="pk"
                type="text"
                value={form.publishable_key}
                onChange={(e) => setForm((p) => ({ ...p, publishable_key: e.target.value }))}
                placeholder="pk_live_…"
                required
                className="font-mono text-sm"
                data-testid="input-stripe-publishable-key"
              />
            </div>
            <div>
              <Label htmlFor="wh">Webhook signing secret (whsec_…)</Label>
              <div className="relative">
                <Input
                  id="wh"
                  type={showSecrets.webhook_secret ? 'text' : 'password'}
                  value={form.webhook_secret}
                  onChange={(e) => setForm((p) => ({ ...p, webhook_secret: e.target.value }))}
                  placeholder="whsec_…"
                  required
                  className="font-mono text-sm pr-10"
                  data-testid="input-stripe-webhook-secret"
                />
                <button
                  type="button"
                  onClick={() => setShowSecrets((p) => ({ ...p, webhook_secret: !p.webhook_secret }))}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  {showSecrets.webhook_secret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <Alert className="border-amber-200 bg-amber-50">
              <AlertTriangle className="h-4 w-4 text-amber-700" />
              <AlertDescription className="text-amber-800 text-xs">
                Une fois enregistrée, la nouvelle config est utilisée immédiatement. Les abonnements en cours de checkout au moment du changement utiliseront l'ancienne clé.
              </AlertDescription>
            </Alert>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setEditOpen(false)} disabled={saving}>Annuler</Button>
              <Button type="submit" className="bg-[#52a675] hover:bg-[#3d8a5c]" disabled={saving} data-testid="btn-stripe-save">
                {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Validation Stripe…</> : <><Save className="w-4 h-4 mr-1" /> Tester & enregistrer</>}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StripeConfigPage;
